import { useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { SignupRequest } from '@medtrack/shared';

import { api, ApiClientError } from '@/lib/api';
import { useSeedSession, useSession, type SessionPayload } from '@/lib/session';
import { detectTimezone, timezoneOptions } from '@/lib/timezones';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { InlineError } from '@/components/ui/error-state';
import { AuthLayout } from './AuthLayout';
import { PasswordField, PASSWORD_MIN_LENGTH } from './PasswordField';
import { useAuthConfig } from './authConfig';

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const seedSession = useSeedSession();
  const session = useSession();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const authConfig = useAuthConfig();

  /**
   * Pre-fill the timezone from the browser, but leave it editable.
   *
   * This is not a convenience field. A profile's timezone is what "08:00" means
   * for every dose it ever schedules (CONTRACT §2) — get it wrong and a whole
   * regimen shifts. Detection is right almost always and wrong in exactly the
   * cases that matter: someone setting up an account for a parent in another
   * country, or signing up from a hotel. So: detect, show what was detected,
   * and let them change it.
   */
  const [timezone, setTimezone] = useState<string>(() => detectTimezone());
  const detected = useMemo(() => detectTimezone(), []);
  const options = useMemo(() => timezoneOptions(), []);

  const mutation = useMutation<SessionPayload, ApiClientError, SignupRequest>({
    mutationFn: (body) => api.post<SessionPayload>('/auth/signup', body),
    retry: false,
    onSuccess: (payload) => {
      seedSession(payload);
      navigate('/', { replace: true });
    },
  });

  // Shown when the server says so, AND whenever we could not find out, AND
  // whenever the server has just complained about it. Hiding the field while
  // the config is unknown would let the form fail on a code the person was
  // never asked for, with nowhere to show the error. A server in open mode
  // simply ignores an empty invite code.
  const needsInvite =
    authConfig.data?.signup === 'invite' || authConfig.isError || Boolean(mutation.error?.fields?.inviteCode);

  if (session.isSuccess && !mutation.isPending) {
    return <Navigate to="/" replace />;
  }

  const fields = mutation.error?.fields;
  const generalError =
    mutation.error && !mutation.error.isValidation ? mutation.error : null;

  // Client-side check only so the button does not fire an obviously invalid
  // request. The server's schema is the authority and its errors win.
  const passwordTooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (mutation.isPending) return;
    mutation.mutate({
      email: email.trim(),
      password,
      displayName: displayName.trim(),
      timezone,
      ...(needsInvite ? { inviteCode: inviteCode.trim() } : {}),
    });
  };

  if (authConfig.data?.signup === 'closed') {
    return (
      <AuthLayout
        title="New accounts are closed"
        subtitle="Medication Tracker"
        footer={
          <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-ink">
          This server is not taking new accounts right now. Ask the person who runs the app to set
          one up for you, or to give you an invite code.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Self-hosted medication tracking"
      footer={
        <>
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-accent underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {generalError ? <InlineError error={generalError} /> : null}

        {needsInvite ? (
          <Input
            label="Invite code"
            name="inviteCode"
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value)}
            error={fields?.inviteCode}
            hint="The person who runs this app will give you this code."
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={200}
            required
          />
        ) : null}

        <Input
          label="Your name"
          name="displayName"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={fields?.displayName}
          autoComplete="name"
          maxLength={120}
          required
          autoFocus
        />

        <Input
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fields?.email}
          autoComplete="username"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          maxLength={254}
          required
        />

        <PasswordField
          name="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={
            fields?.password ??
            (passwordTooShort
              ? `Use at least ${PASSWORD_MIN_LENGTH} characters.`
              : undefined)
          }
          showMinLengthHint
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          maxLength={200}
          required
        />

        <Select
          label="Timezone"
          value={timezone}
          onValueChange={setTimezone}
          options={options}
          error={fields?.timezone}
          hint={
            timezone === detected
              ? 'Detected from this device. Doses are scheduled in this zone.'
              : 'Doses are scheduled in this zone.'
          }
          position="popper"
          required
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={mutation.isPending}
          disabled={passwordTooShort || (authConfig.data?.signup === 'invite' && inviteCode.trim().length === 0)}
          className="w-full"
        >
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export default SignupPage;
